import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Lock, User, Building2, FileText, Phone, MapPin, Sparkles, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import CinematicNav from "@/components/CinematicNav";

/**
 * Signup cinematic — registrazione CENTRALIZZATA Hub Identity.
 *
 * Un solo account TotalKlick → accesso via SSO a:
 *   HUB CENTRALE        TotalPL · TotalKlickADS · Design & Stampa · Aria Core  (totalklick.it)
 *   PRODOTTI AUTONOMI   OfferteInCittà · EllyMenu · GoStreet · PhotoVendo      (domini dedicati)
 *
 * I campi tenant (P.IVA, indirizzo) restano per attivare TotalPL/Design.
 * Possono essere lasciati vuoti se l'utente vuole solo Aria/ADS o moduli autonomi.
 */

const MODULES_PREVIEW = [
    { id: "totalpl", name: "TotalPL", color: "#FF1A5C" },
    { id: "ads", name: "TotalKlickADS", color: "#7C3AED" },
    { id: "design", name: "Design & Stampa", color: "#06B6D4" },
    { id: "aria", name: "Aria Core", color: "#4F8BFF" },
    { id: "offerte", name: "OfferteInCittà", color: "#FBBF24", external: true },
    { id: "elly", name: "EllyMenu", color: "#E53935", external: true },
    { id: "gostreet", name: "GoStreet", color: "#10B981", external: true },
    { id: "photovendo", name: "PhotoVendo", color: "#A855F7", external: true },
];

export default function Signup() {
    const { registerTenant } = useAuth();
    const nav = useNavigate();
    const canvasRef = useRef(null);

    const [form, setForm] = useState({
        tenant_name: "",
        admin_first_name: "",
        admin_last_name: "",
        admin_cf: "",
        admin_email: "",
        admin_password: "",
        vat: "",
        address: "",
        civic: "",
        city: "",
        cap: "",
        phone_fixed: "",
        phone_mobile: "",
        gdpr_consent: true,
        marketing_consent: true,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showOptional, setShowOptional] = useState(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Starfield background
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf = 0;
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);
        const stars = Array.from({ length: 220 }, () => ({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            r: Math.random() * 1.2 + 0.3,
            s: Math.random() * 0.04 + 0.015,
            o: Math.random() * 0.6 + 0.15,
        }));
        const loop = () => {
            ctx.fillStyle = "#05050F";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            stars.forEach((st) => {
                ctx.fillStyle = `rgba(180,200,255,${st.o})`;
                ctx.beginPath();
                ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
                ctx.fill();
                st.y += st.s;
                if (st.y > canvas.height) st.y = 0;
            });
            raf = requestAnimationFrame(loop);
        };
        loop();
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.gdpr_consent) {
            setError("Devi accettare l'informativa Privacy/GDPR per registrarti.");
            return;
        }
        setLoading(true);
        try {
            await registerTenant({
                ...form,
                // Se l'utente non vuole TotalPL, popoliamo tenant_name con nome+cognome come fallback
                tenant_name: form.tenant_name?.trim() || `${form.admin_first_name} ${form.admin_last_name}`.trim() || form.admin_email,
            });
            nav("/app");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative min-h-screen w-full overflow-hidden"
            style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }}
            data-testid="signup-page"
        >
            <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} />
            <div
                className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[700px] pointer-events-none"
                style={{ background: "radial-gradient(ellipse, rgba(79,139,255,0.22) 0%, transparent 65%)", filter: "blur(50px)", zIndex: 0 }}
            />

            <CinematicNav />

            <div className="relative z-10 min-h-screen flex items-start justify-center px-5 pt-24 pb-16">
                <div className="w-full max-w-5xl">
                    {/* Header */}
                    <div className="text-center mb-10">
                        <div
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                            style={{ background: "rgba(79,139,255,0.08)", border: "1px solid rgba(79,139,255,0.22)" }}
                        >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#4F8BFF", boxShadow: "0 0 8px #4F8BFF" }} />
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#4F8BFF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                identity centrale · single sign-on
                            </span>
                        </div>
                        <h1
                            className="text-white tracking-tight"
                            style={{
                                fontFamily: "Geist, Outfit, sans-serif",
                                fontSize: "clamp(2.2rem, 4.6vw, 3.4rem)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.03em",
                                fontWeight: 200,
                            }}
                        >
                            Entra nell&apos;<span style={{ fontWeight: 600 }}>Ecosistema</span>.
                        </h1>
                        <p className="mt-4 text-white/60 text-[15px] max-w-2xl mx-auto" style={{ fontFamily: "Outfit, sans-serif", lineHeight: 1.55 }}>
                            Un solo account ti dà accesso a <b className="text-white">tutti gli 8 moduli</b> dell&apos;ecosistema TotalKlick.
                            Anche quelli su dominio dedicato.
                        </p>

                        {/* Modules pills */}
                        <div className="flex flex-wrap justify-center gap-1.5 mt-7 max-w-3xl mx-auto" data-testid="signup-modules-preview">
                            {MODULES_PREVIEW.map((m) => (
                                <span
                                    key={m.id}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] text-white/75"
                                    style={{
                                        background: `${m.color}11`,
                                        border: `1px solid ${m.color}33`,
                                        fontFamily: "Geist, sans-serif",
                                    }}
                                    data-testid={`signup-module-pill-${m.id}`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                                    {m.name}
                                    {m.external && <span className="opacity-50 text-[9px] uppercase tracking-wider">ext</span>}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Form */}
                    <div
                        className="rounded-3xl p-7 md:p-10"
                        style={{
                            background: "rgba(15,18,36,0.65)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            backdropFilter: "blur(24px) saturate(140%)",
                            WebkitBackdropFilter: "blur(24px) saturate(140%)",
                            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
                        }}
                    >
                        <form onSubmit={submit} className="space-y-7" data-testid="signup-form">
                            {/* Section: Identità */}
                            <Section title="01" subtitle="La tua identità">
                                <div className="grid md:grid-cols-2 gap-3">
                                    <Field label="Nome *" icon={User}>
                                        <input data-testid="signup-first-name" required value={form.admin_first_name} onChange={(e) => set("admin_first_name", e.target.value)} placeholder="Antonio" className="cine-input pl-11" />
                                    </Field>
                                    <Field label="Cognome *" icon={User}>
                                        <input data-testid="signup-last-name" required value={form.admin_last_name} onChange={(e) => set("admin_last_name", e.target.value)} placeholder="Rossi" className="cine-input pl-11" />
                                    </Field>
                                    <Field label="Email *" icon={Mail}>
                                        <input data-testid="signup-email" type="email" required autoComplete="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} placeholder="tu@esempio.it" className="cine-input pl-11" />
                                    </Field>
                                    <Field label="Password * (min 8)" icon={Lock}>
                                        <input data-testid="signup-password" type="password" required minLength={8} autoComplete="new-password" value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} placeholder="••••••••" className="cine-input pl-11" />
                                    </Field>
                                </div>
                            </Section>

                            {/* Section: Attività (opzionale, espandibile) */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setShowOptional((v) => !v)}
                                    className="w-full text-left flex items-center justify-between rounded-xl px-4 py-3 transition hover:bg-white/3"
                                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                                    data-testid="signup-toggle-optional"
                                >
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>02</span>
                                            <span className="text-white text-[14.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                                Dati attività <span className="text-white/40 text-[12px]">(per TotalPL e Design & Stampa)</span>
                                            </span>
                                        </div>
                                        <p className="text-[12px] text-white/45 mt-1 ml-12" style={{ fontFamily: "Outfit, sans-serif" }}>
                                            Salta se ora vuoi solo Aria, ADS o moduli autonomi. Puoi aggiungerli quando attivi un modulo.
                                        </p>
                                    </div>
                                    <span className="text-white/45 text-[18px]">{showOptional ? "−" : "+"}</span>
                                </button>

                                {showOptional && (
                                    <div className="mt-5 space-y-5" data-testid="signup-optional-fields">
                                        <div className="grid md:grid-cols-2 gap-3">
                                            <Field label="Nome attività" icon={Building2}>
                                                <input data-testid="signup-tenant-name" value={form.tenant_name} onChange={(e) => set("tenant_name", e.target.value)} placeholder="es. Momento Grafico" className="cine-input pl-11" />
                                            </Field>
                                            <Field label="P.IVA" icon={FileText}>
                                                <input data-testid="signup-vat" value={form.vat} onChange={(e) => set("vat", e.target.value)} placeholder="IT12345678901" className="cine-input pl-11" />
                                            </Field>
                                            <Field label="C.F. titolare" icon={FileText}>
                                                <input data-testid="signup-cf" value={form.admin_cf} onChange={(e) => set("admin_cf", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501Z" className="cine-input pl-11 uppercase" />
                                            </Field>
                                            <Field label="Cellulare" icon={Phone}>
                                                <input data-testid="signup-phone-mobile" value={form.phone_mobile} onChange={(e) => set("phone_mobile", e.target.value)} placeholder="338 1234567" className="cine-input pl-11" />
                                            </Field>
                                        </div>
                                        <div className="grid grid-cols-12 gap-3">
                                            <div className="col-span-12 md:col-span-7">
                                                <Field label="Via" icon={MapPin}>
                                                    <input data-testid="signup-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="es. Corso A. Volta" className="cine-input pl-11" />
                                                </Field>
                                            </div>
                                            <div className="col-span-4 md:col-span-2">
                                                <Field label="Civico">
                                                    <input data-testid="signup-civic" value={form.civic} onChange={(e) => set("civic", e.target.value)} placeholder="26" className="cine-input" />
                                                </Field>
                                            </div>
                                            <div className="col-span-8 md:col-span-3">
                                                <Field label="CAP">
                                                    <input data-testid="signup-cap" value={form.cap} onChange={(e) => set("cap", e.target.value)} placeholder="80040" className="cine-input" />
                                                </Field>
                                            </div>
                                            <div className="col-span-12">
                                                <Field label="Città">
                                                    <input data-testid="signup-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Terzigno (NA)" className="cine-input" />
                                                </Field>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Consents */}
                            <div className="space-y-3 pt-4 border-t border-white/8">
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <input
                                        data-testid="signup-gdpr"
                                        type="checkbox"
                                        checked={form.gdpr_consent}
                                        onChange={(e) => set("gdpr_consent", e.target.checked)}
                                        className="mt-1 w-4 h-4 cursor-pointer"
                                        style={{ accentColor: "#4F8BFF" }}
                                    />
                                    <span className="text-[12.5px] leading-[1.55] text-white/65" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        <b className="text-white">Consenso GDPR (obbligatorio)</b> — Accetto l&apos;
                                        <Link to="/legal/privacy" target="_blank" className="text-[#4F8BFF] hover:text-white font-medium">Informativa Privacy</Link> ai sensi del Reg. UE 2016/679 e
                                        il trattamento dei miei dati da parte di <b className="text-white">TotalKlick srls</b> (P.IVA 10559691216) per la fornitura dei servizi dell&apos;ecosistema.
                                    </span>
                                </label>
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <input
                                        data-testid="signup-marketing"
                                        type="checkbox"
                                        checked={form.marketing_consent}
                                        onChange={(e) => set("marketing_consent", e.target.checked)}
                                        className="mt-1 w-4 h-4 cursor-pointer"
                                        style={{ accentColor: "#4F8BFF" }}
                                    />
                                    <span className="text-[12.5px] leading-[1.55] text-white/65" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        <b className="text-white">Comunicazioni commerciali (facoltativo)</b> — Acconsento a ricevere offerte e novità relative ai moduli dell&apos;ecosistema TotalKlick. Posso revocare in qualsiasi momento.
                                    </span>
                                </label>
                            </div>

                            {error && (
                                <div
                                    data-testid="signup-error"
                                    className="px-3 py-2.5 rounded-lg text-[13px]"
                                    style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.3)", color: "#FCA5A5" }}
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                data-testid="signup-submit"
                                type="submit"
                                disabled={loading}
                                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                                style={{
                                    background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.04em",
                                    boxShadow: "0 18px 60px -12px rgba(79,139,255,0.6)",
                                }}
                            >
                                {loading ? "Creazione Workspace…" : <>Crea il tuo Workspace <ArrowRight size={15} /></>}
                            </button>

                            <p className="text-center text-[12.5px] text-white/50">
                                Hai già un account?{" "}
                                <Link to="/login" data-testid="link-login" className="text-[#4F8BFF] hover:text-white transition font-medium">
                                    Accedi
                                </Link>
                            </p>
                        </form>
                    </div>

                    {/* SSO explanation footer */}
                    <div
                        className="mt-8 grid md:grid-cols-3 gap-3 text-[12px]"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        <InfoCard
                            icon={<Check size={14} className="text-[#4F8BFF]" />}
                            title="Identity centrale"
                            body="Un solo account, gestito da totalklick.it. Le tue credenziali non sono duplicate da nessuna parte."
                        />
                        <InfoCard
                            icon={<Sparkles size={14} className="text-[#FBBF24]" />}
                            title="Single sign-on cross-domain"
                            body="Lo stesso login funziona su offerteincitta.it, gostreet.it, photovendo.com, elly.offerteincitta.it tramite il nostro IdP RS256."
                        />
                        <InfoCard
                            icon={<Building2 size={14} className="text-[#10B981]" />}
                            title="Made in Italy · GDPR"
                            body="TotalKlick srls — Terzigno (NA). Tutti i dati restano in Europa. Conforme AI Act e GDPR."
                        />
                    </div>
                </div>
            </div>

            <style>{`
                .cine-input {
                    width: 100%;
                    padding: 12px 14px;
                    border-radius: 12px;
                    border: 1px solid rgba(255,255,255,0.1);
                    background: rgba(255,255,255,0.03);
                    color: #fff;
                    font-family: 'Geist', 'Outfit', sans-serif;
                    font-size: 14px;
                    transition: all 0.2s;
                }
                .cine-input:focus {
                    outline: none;
                    border-color: rgba(79,139,255,0.55);
                    background: rgba(255,255,255,0.05);
                    box-shadow: 0 0 0 3px rgba(79,139,255,0.12);
                }
                .cine-input::placeholder { color: rgba(255,255,255,0.3); }
                input.cine-input.pl-11, textarea.cine-input.pl-11 { padding-left: 44px; }
                input.cine-input.pr-11, textarea.cine-input.pr-11 { padding-right: 44px; }
            `}</style>
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <span
                    className="text-[10px] uppercase tracking-[0.22em] text-white/40"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                >
                    {title}
                </span>
                <span
                    className="text-white text-[14.5px]"
                    style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}
                >
                    {subtitle}
                </span>
            </div>
            {children}
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <div>
            <label
                className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/45"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
            >
                {label}
            </label>
            <div className="relative">
                {Icon && <Icon size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />}
                {children}
            </div>
        </div>
    );
}

function InfoCard({ icon, title, body }) {
    return (
        <div
            className="rounded-xl p-4"
            style={{
                background: "rgba(15,18,36,0.55)",
                border: "1px solid rgba(255,255,255,0.06)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
            }}
        >
            <div className="flex items-center gap-2 mb-2 text-white text-[12.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                {icon} {title}
            </div>
            <p className="text-white/55 text-[12px] leading-relaxed">{body}</p>
        </div>
    );
}
